import type { PlantEdge, PlantPartRef } from '@axilon-apps/domains-ontology-ui-state'

/** Max recursion depth for the BOM tree UI (mirrors StructureTree). */
export const BOM_TREE_DEPTH_GUARD = 24

export interface BomTreeNode {
  part: PlantPartRef
  /** `HAS_PART` edge from the parent (equipment or part) to this part. */
  edge: PlantEdge
  children: BomTreeNode[]
}

/**
 * Stitch flat `bomParts` + `bomEdges` into a forest rooted at the
 * equipment. Roots are edges whose `source` is the equipment id.
 */
export function buildBomTree(
  equipmentId: string,
  parts: PlantPartRef[],
  edges: PlantEdge[]
): BomTreeNode[] {
  const partById = new Map(parts.map((part) => [part.id, part]))
  const childrenByParent = new Map<string, PlantEdge[]>()
  for (const edge of edges) {
    if (edge.type !== 'HAS_PART') continue
    const list = childrenByParent.get(edge.source) ?? []
    list.push(edge)
    childrenByParent.set(edge.source, list)
  }

  const build = (parentId: string, depth: number): BomTreeNode[] => {
    if (depth > BOM_TREE_DEPTH_GUARD) return []
    const childEdges = childrenByParent.get(parentId) ?? []
    const nodes: BomTreeNode[] = []
    for (const edge of childEdges) {
      const part = partById.get(edge.target)
      if (!part) continue
      nodes.push({
        part,
        edge,
        children: build(part.id, depth + 1)
      })
    }
    return nodes.sort(
      (a, b) => a.part.name.localeCompare(b.part.name) || a.part.id.localeCompare(b.part.id)
    )
  }

  return build(equipmentId, 0)
}

export function readBomQuantity(edge: PlantEdge): number | undefined {
  const value = edge.props['quantity']
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

export function readBomAssemblyPath(edge: PlantEdge): string | undefined {
  const value = edge.props['assembly_path']
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

export function readBomOptional(edge: PlantEdge): boolean {
  return edge.props['is_optional'] === true
}

/** Depth-first flatten of a BOM forest for table rendering. */
export function flattenBomTree(
  roots: BomTreeNode[],
  depth = 0
): Array<{ node: BomTreeNode; depth: number }> {
  const rows: Array<{ node: BomTreeNode; depth: number }> = []
  for (const node of roots) {
    rows.push({ node, depth })
    if (depth < BOM_TREE_DEPTH_GUARD && node.children.length > 0) {
      rows.push(...flattenBomTree(node.children, depth + 1))
    }
  }
  return rows
}
